//#region types
import { AuthEnums } from "models/enums";
import {
  WintSuperkollAPIModel as ApiModels,
  WintSuperkollAPIModelWintSalaryCommission as WintSalaryApiModels,
  WintUtilsWebApi,
} from "wint-api-client/dist/types";
import { CommissionSalaryFormTypes } from "models/formTypes";
import { AxiosResponse } from "axios";
import useAPIClientHelper from "./useAPIClientHelper";
//#endregion
import useSalaryPermissions from "components/salary/hooks/useSalaryPermissions";
import { DateHelper } from "utils/helpers";

/**
 * One row shape for the commission report, whichever engine produced it. The two endpoints disagree
 * on the commission field's name and — more importantly — on the sign of the amount, so both are
 * normalised here rather than branched inside the table.
 */
export interface CommissionSalaryRow {
  id?: string;
  personId: number;
  personName?: string;
  comment?: string;
  date: Date;
  /** Signed for display: positive for a sale, negative for a reversal or a cost. */
  amount?: number;
  commission: number;
}

const mapLegacyTransaction = (transaction: ApiModels.CommissionSalaryEmployeeTransaction): CommissionSalaryRow => ({
  id: transaction.id,
  personId: transaction.personId,
  personName: transaction.personName,
  comment: transaction.comment,
  date: transaction.date,
  // The legacy endpoint returns the raw ledger amount, where revenue is a credit and therefore
  // negative, so the sign is flipped for display exactly as the table used to do inline. Zero is left
  // as is: negating it gives -0, which the currency formatter prints as "−0 kr".
  amount: transaction.amount ? -transaction.amount : transaction.amount,
  commission: transaction.salary,
});

const mapWintSalaryTransaction = (transaction: WintSalaryApiModels.CommissionTransaction): CommissionSalaryRow => ({
  id: transaction.id,
  personId: transaction.personId,
  personName: transaction.personName,
  comment: transaction.comment,
  date: transaction.date,
  // Wint.Salary already applies the company's payout-basis sign server-side
  // (CommissionTransactionMapper multiplies by salesSign), so flipping it again here would render
  // every sale as negative.
  amount: transaction.amount,
  commission: transaction.commission,
});

/**
 * Maps the table's sort key onto the legacy Core DTO's field name. Only the commission column
 * diverges ("commission" on the normalised row / Wint.Salary DTO vs "salary" on the legacy DTO);
 * every other column key already matches and FilterLogic.Select sorts on any DTO property, so the
 * rest passes through unchanged.
 */
const toLegacySortProperty = (property?: string): string | undefined =>
  property?.toLowerCase() === "commission" ? "Salary" : property;

/**
 * Maps the table's sort key onto the fields Wint.Salary's CommissionBrokerViewService actually
 * honours (PersonName, BookingDate, Amount, Commission, YearAndMonth — case-sensitive). The only
 * diverging column is the date one, whose key pascalizes to "Date" but must be "BookingDate";
 * PersonName/Amount/Commission already match. The comment column is not sortable server-side there
 * (its sort is disabled in the view) and ThenByProperty is ignored, so both fall back to the default
 * order (BookingDate desc, then RowOrder).
 */
const toWintSalarySortProperty = (property?: string): string | undefined =>
  property?.toLowerCase() === "date" ? "BookingDate" : property;

type CommissionSalaryRowsResponse = AxiosResponse<WintUtilsWebApi.ListResponse<CommissionSalaryRow>> & {
  key?: number;
};

/**
 * Replaces the paged response's rows with normalised ones, leaving the paging fields untouched so
 * the table keeps working. summaryItem is mapped rather than spread through: it shares the row type,
 * so leaving the source object in place would type-mismatch. Nothing requests summaries today.
 */
const mapListResponse = <TSource,>(
  response: { data?: WintUtilsWebApi.ListResponse<TSource>; key?: number },
  mapItem: (item: TSource) => CommissionSalaryRow
): CommissionSalaryRowsResponse => {
  const { data, ...rest } = response;
  return {
    ...(rest as Omit<CommissionSalaryRowsResponse, "data">),
    data: {
      ...data,
      numPerPage: data?.numPerPage ?? 0,
      page: data?.page ?? 0,
      totalItems: data?.totalItems ?? 0,
      totalItemsWithOutFilter: data?.totalItemsWithOutFilter ?? 0,
      items: (data?.items ?? []).map(mapItem),
      summaryItem: data?.summaryItem ? mapItem(data.summaryItem) : undefined,
    },
  };
};

function useCommissionSalaryService() {
  const { apiClientInstance } = useAPIClientHelper();
  const { isWintSalaryCommissionAvailable } = useSalaryPermissions();

  const filter = (filter: ApiModels.CommissionSalaryEmployeeTransactionFilter) => {
    return apiClientInstance(AuthEnums.AuthType.Company).CommissionSalaryReportClient.GetEmployeeTransactions(filter);
  };

  const wintSalaryFilter = (filter: WintSalaryApiModels.CommissionTransactionFilter) => {
    return apiClientInstance(AuthEnums.AuthType.Company).WintSalaryCommissionClient.GetTransactions(filter);
  };

  /**
   * Reads from Wint.Salary for a migrated company and from the legacy Core engine otherwise, mapping
   * either into {@link CommissionSalaryRow}. Core scopes a non-admin to their own rows server-side,
   * so the same call serves both the broker self-view and the salary-admin view.
   */
  const reportFilter = async (
    baseFilter: WintUtilsWebApi.Filter,
    formFilter: CommissionSalaryFormTypes.Filter
  ): Promise<CommissionSalaryRowsResponse> => {
    const yearMonth = formFilter.yearAndMonth ? DateHelper.getYearMonth(formFilter.yearAndMonth) : 0;
    const personId = formFilter.personId ?? 0;

    if (isWintSalaryCommissionAvailable) {
      const wintSalaryBaseFilter: WintUtilsWebApi.Filter = {
        ...baseFilter,
        orderByProperty: toWintSalarySortProperty(baseFilter.orderByProperty),
        thenByProperty: toWintSalarySortProperty(baseFilter.thenByProperty),
      };
      const response = await wintSalaryFilter({ ...wintSalaryBaseFilter, yearMonth, personId });
      return mapListResponse(response, mapWintSalaryTransaction);
    }

    // The table derives its server sort key from the column key ("commission"), but the legacy Core
    // DTO exposes that value as "salary". Translate the sort property for the legacy endpoint only
    // (Wint.Salary already sorts by "commission"); sort direction is left untouched.
    const legacyBaseFilter: WintUtilsWebApi.Filter = {
      ...baseFilter,
      orderByProperty: toLegacySortProperty(baseFilter.orderByProperty),
      thenByProperty: toLegacySortProperty(baseFilter.thenByProperty),
    };
    const response = await filter({ ...legacyBaseFilter, yearAndMonth: yearMonth, personId });
    return mapListResponse(response, mapLegacyTransaction);
  };

  return {
    filter,
    wintSalaryFilter,
    reportFilter,
  };
}

export default useCommissionSalaryService;
